import type React from "react";
import { useState } from "react";
import { Box, Typography, TextField, Divider } from "@mui/material";
import type { Proposal } from "../types/proposal.types";

interface AgreementFormPageProps {
  proposal: Proposal;
}

const AgreementFormPage: React.FC<AgreementFormPageProps> = ({ proposal }) => {
  const [formData, setFormData] = useState({
    eventName: proposal.deceasedName || proposal.eventTitle || "",
    clientContact: proposal.client.name,
    email: proposal.client.email,
    eventDate: proposal.booking?.services?.[0].date_of_service || "",
    internetAgreement: false,
    copyrightAgreement: false,
    clientSignature: "",
    clientSignatureDate: "",
  });

  return (
    <Box id="agreement-page" sx={{ p: 6 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Internet Agreement & Copyright Disclaimer Form
        </Typography>
      </Box>

      {/* Form Fields */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="standard"
          label="Event Details / Name"
          value={formData.eventName}
          onChange={(e) =>
            setFormData({ ...formData, eventName: e.target.value })
          }
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          variant="standard"
          label="Client Contact"
          value={formData.clientContact}
          onChange={(e) =>
            setFormData({ ...formData, clientContact: e.target.value })
          }
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          variant="standard"
          label="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          variant="standard"
          label="Event Date"
          type="date"
          value={formData.eventDate}
          onChange={(e) =>
            setFormData({ ...formData, eventDate: e.target.value })
          }
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Internet Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}
        >
          INTERNET
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8 }}>
          As The Livestreaming Company, we advise utilising a wired ethernet
          connection to maximise the stability of the internet connection during
          the broadcast. While we recommend this, please note that even with a
          wired connection, interruptions may still occur.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8 }}>
          I, <TextField variant="standard" sx={{ width: 200, mx: 1 }} />, fully
          understand that while a wired internet connection can enhance
          stability, it is not infallible and may experience disruptions,
          potentially affecting the broadcast quality.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Copyright Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}
        >
          COPYRIGHT DISCLAIMER
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8 }}>
          I, <TextField variant="standard" sx={{ width: 200, mx: 1 }} />,
          acknowledge that playing copyrighted music during the livestream
          without proper licensing or permission may lead to copyright strikes
          on platforms such as YouTube. The Livestreaming Company will not be
          held liable for any copyright infringements occurring during the
          broadcast.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8 }}>
          Furthermore, I understand that segments of the livestream containing
          copyrighted music may be muted, and the video might be made
          unavailable by YouTube due to copyright infringement claims.
        </Typography>
      </Box>

      {/* Signatures */}
      <Box sx={{ mt: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <Box sx={{ width: "45%" }}>
            <TextField
              fullWidth
              label="Signed"
              variant="standard"
              value={formData.clientSignature}
              onChange={(e) =>
                setFormData({ ...formData, clientSignature: e.target.value })
              }
            />
          </Box>
          <Box sx={{ width: "45%" }}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              variant="standard"
              value={formData.clientSignatureDate}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  clientSignatureDate: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          pt: 3,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption">www.muzukuru.com</Typography>
      </Box>
    </Box>
  );
};

export default AgreementFormPage;
